/* eslint-disable import/no-unresolved */
import { faPersonSign } from '@fortawesome/pro-regular-svg-icons';
import { SwiperSlide } from 'swiper/react';
import DateSelect from '../../../components/DateSelect';
import Gallery from '../../../components/Gallery';
import MapContainer from '../../../components/MapContainer';
import MeetGuides from '../../../components/MeetGuides';
import Slider from '../../../components/Slider';
import GenericCard from '../../../components/GenericCard';
import client from '../../../apollo-client';
import { GET_TRIPS_BY_SLUG, GET_TRIPS_EXCEPT, GET_TRIPS_SLUG } from '../../../graphql/trips';
import CancellationPolicy from '../../../components/CancellationPolicy';
import BasicTripInfo from '../../../components/BasicTripInfo';
import Highlights from '../../../components/TopicsCardGrid';
import { useMediaQuery } from 'beautiful-react-hooks';
import { CheckoutContext } from '../../../context/checkout';
import { useContext, useEffect } from 'react';
import TripCard from '../../../components/TripCard';
// import { Impact } from '../../../components/Impact';
import UpcomingDates from '../../../components/UpcomingDates';
import { formatPrice } from '../../../utils/formatPrice';
import Link from 'next/link';
import { GET_CANCELLATION_POLICY } from '../../../graphql/cancellation-policy';

export default function Trip({ trip, relatedTrips, cancellationPolicy }) {
  const { setTrip } = useContext(CheckoutContext);
  const isMobile = useMediaQuery('(max-width: 768px)');
  useEffect(() => {
    setTrip(trip);
  }, [setTrip, trip]);

  if (!trip) return;

  const highlights =
    trip?.attributes?.highlights?.map((item) => ({ id: item.id, attributes: { ...item } })) || [];
  const tripFeatures = trip?.attributes?.trip_features?.data || [];
  return (
    <div className="relative">
      {trip?.attributes?.events?.length ? (
        <div className="fixed bottom-0 z-50 mt-10 mb-0 w-full bg-white lg:hidden">
          <div className="flex items-center py-4 px-6">
            <div>
              <span className="font-poppins text-base font-semibold leading-[22.4px] text-dark-800">
                {formatPrice(trip?.attributes?.display_price)}
              </span>
              <span className="font-sans text-base font-normal leading-[22.4px]"> / person</span>
            </div>
            <div className="ml-auto">
              <Link href={`/trips/${trip?.attributes?.slug}/checkout`}>
                <button
                  className="rounded-full bg-tertiary-main px-[24px] py-[14px] font-poppins text-base font-medium"
                  type="button"
                >
                  Show Dates
                </button>
              </Link>
            </div>
          </div>
        </div>
      ) : null }

      <div className="content-wrapper relative mx-auto min-h-screen pt-[86px] text-dark-800 lg:pt-32">
        <div className={isMobile ? 'hero-image-component w-screen' : ''}>
          <Gallery trip={trip} />
        </div>
        <div className="items-baseline md:grid md:grid-cols-3 md:justify-center">
          <div className="max-w-[730px] md:col-span-2">
            <BasicTripInfo trip={trip} />
            {highlights?.length ? (
              <Highlights popup="true" data={highlights} title="Highlights of the trip" alignment />
            ) : null}

            <Slider
              slidesPerView={'auto'}
              spaceBetween={isMobile ? 10 : 16.5}
              title="What’s included"
            >
              {tripFeatures.map((item) => (
                <SwiperSlide key={item.id}>
                  <GenericCard
                    icon={faPersonSign}
                    title={item.attributes.name}
                    description={item.attributes.description}
                    small
                  />
                </SwiperSlide>
              ))}
            </Slider>
            <MeetGuides trip={trip} />
            {/* <Impact ngo={trip?.attributes?.ngo} /> */}
          </div>
          <div id="dateSelecter" className="md:sticky md:top-[10%]">
            {trip?.attributes.events.length !== 0 ? <DateSelect trip={trip} /> : <UpcomingDates />}
          </div>
        </div>
        <div className="relative">
          <MapContainer trip={trip} />
          <CancellationPolicy data={cancellationPolicy} TripMargin/>
          <Slider
            slidesPerView={'auto'}
            spaceBetween={isMobile ? 16 : 49.5}
            title="More Nature Meets in your area"
            marginTop
          >
            {relatedTrips.map((item) => (
              <SwiperSlide key={item.id}>
                <TripCard trip={item} />
              </SwiperSlide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
}

export async function getStaticProps({ params }) {
  const { slug } = params;
  const startDate = new Date();
  startDate.setUTCHours(0, 0, 0, 0);
  const res = await client.query({
    query: GET_TRIPS_BY_SLUG,
    variables: { slug, startDate: startDate.toISOString() },
  });
  const cancellationPolicy = await client.query({ query: GET_CANCELLATION_POLICY });
  const trip = (res?.data?.trips?.data || [])[0];
  let relatedTrips = [];
  if (trip) {
    const output = await client.query({ query: GET_TRIPS_EXCEPT, variables: { id: trip.id } });
    relatedTrips = output?.data?.trips?.data || [];
  }
  return {
    props: {
      ...params,
      trip,
      relatedTrips,
      footerPadding: true,
      cancellationPolicy: cancellationPolicy?.data?.cancellationPolicy?.data || null,
    },
    revalidate: Number(process.env.REVALIDATE_TIME_SECONDS),
  };
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export async function getStaticPaths() {
  // get all trips slug
  const trips = [];
  let offset = 0;
  const limit = 100;
  let results = [];
  do {
    const res = await client.query({ query: GET_TRIPS_SLUG, variables: { start: offset, limit } });
    results = res?.data?.trips?.data || [];
    trips.push(...results);
    offset += limit;
  } while (results.length === limit);

  const paths = trips.map((trip) => ({ params: { slug: trip.attributes.slug } }));

  // We'll pre-render only these paths at build time.
  // { fallback: blocking } will server-render pages
  // on-demand if the path doesn't exist.
  return {
    paths,
    fallback: false,
  };
}
